import React, { useState, useEffect } from 'react'; // Added useEffect
import axios from 'axios'; // Added axios
import { motion, AnimatePresence } from 'framer-motion';
import { Maximize2, Settings, Loader2 } from 'lucide-react'; // Added Loader
import Header from '../Components/Header';
import Footer from '../Components/Footer';
import image1 from '../Assets/Home/Home_3.jpg';

const GalleryPage = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [images, setImages] = useState([]); // State for DB data
  const [loading, setLoading] = useState(true);

  // FETCH DATA FROM BACKEND
  useEffect(() => {
    document.title = "Marvel Sato | Gallery";
    const fetchGallery = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/gallery/gallery`);
        setImages(response.data);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching gallery:", error);
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: false, amount: 0.1 },
    transition: { duration: 0.8, ease: "easeOut" }
  };

  // Logic to filter images based on selection
  const filteredImages = activeTab === 'all' 
    ? images 
    : images.filter(img => img.type === activeTab);

  const tabs = [
    { id: 'all', label: 'All Works' },
    { id: 'company', label: 'Company' },
    { id: 'products', label: 'Promotion' },
    { id: 'newspaper', label: 'Newspaper' }
  ];

  if (loading) return (
    <div className="h-screen flex items-center justify-center text-orange-500 bg-white">
      <Loader2 className="animate-spin" size={48} />
    </div> 
  );

  return (
    <div className="bg-white overflow-x-hidden">
      <Header />

      {/* --- HERO SECTION --- */}
      <section className="relative h-[40vh] md:h-[50vh] flex items-center justify-center bg-slate-900 pt-20">
        <div className="absolute inset-0 z-0">
          <img 
            src={image1}
            className="w-full h-full object-cover opacity-20" 
            alt="Gallery Background" 
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-white"></div>
        </div>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
          className="relative z-10 text-center px-4"
        >
          <h1 className="text-4xl md:text-7xl font-black text-slate-900 tracking-tighter italic uppercase">
            Marvel Sato <span className="text-orange-500 not-italic">Gallery</span>
          </h1>
          <div className="h-1.5 w-24 bg-orange-500 mx-auto mt-4 rounded-full"></div>
          <p className="text-slate-500 mt-6 font-bold uppercase tracking-widest text-xs md:text-sm">Visualizing Engineering Excellence</p>
        </motion.div>
      </section>

      {/* --- INTRO TEXT SECTION --- */}
      <section className="py-12 md:py-20 px-6 md:px-24 max-w-5xl mx-auto text-center">
        <motion.div {...fadeInUp}>
          <p className="text-slate-600 text-base md:text-xl leading-relaxed font-medium">
            Explore our <span className="text-slate-900 font-bold">unique collection</span> designed and manufactured to the highest industry standards. 
            Browse through the products that took advantage of the latest technological advancements. 
          </p>
        </motion.div>
      </section>

      {/* --- FILTER NAVIGATION --- */}
      <section className="px-6 md:px-24 mb-12">
        <div className="flex flex-wrap justify-center gap-4 md:gap-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2 rounded-full text-[10px] md:text-xs font-black uppercase tracking-widest transition-all duration-300 ${
                activeTab === tab.id 
                ? 'bg-orange-500 text-white shadow-lg shadow-orange-200' 
                : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      {/* --- GALLERY GRID SECTION --- */}
      <section className="pb-24 px-6 md:px-24 max-w-7xl mx-auto min-h-[600px]">
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          <AnimatePresence mode='popLayout'>
            {filteredImages.map((image, index) => (
              <motion.div
                key={image._id} // Using MongoDB _id
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.4 }}
                className="group relative h-[300px] md:h-[400px] overflow-hidden rounded-[1.5rem] md:rounded-[2.5rem] bg-slate-100 shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer"
              >
                {/* Image with Grayscale Effect */}
                <img 
                  src={image.image} // Map from DB 'image' field
                  alt={image.title}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-110 transition-all duration-1000 ease-in-out"
                />

                {/* Overlay Content */}
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col justify-end p-6 md:p-8">
                  <div className="translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                    <p className="text-orange-500 font-bold text-xs uppercase tracking-widest mb-1">{image.type} Series</p>
                    <div className="flex justify-between items-center">
                      <h3 className="text-white text-xl md:text-2xl font-black italic uppercase leading-none">{image.title}</h3>
                      <div className="bg-orange-500 p-2 md:p-3 rounded-xl text-white shadow-lg">
                        <Maximize2 size={20} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Status Badge */}
                <div className="absolute top-4 left-4">
                  <span className="bg-white/90 backdrop-blur-sm text-slate-900 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-tighter shadow-sm">
                    Marvel Sato <span className="text-orange-600">Certified</span>
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* --- CALL TO ACTION --- */}
      <section className="pb-24 text-center px-6">
        <motion.div {...fadeInUp} className="bg-slate-50 py-16 rounded-[2.5rem] max-w-5xl mx-auto border border-slate-100">
          <h2 className="text-2xl md:text-4xl font-black text-slate-900 mb-4 uppercase italic">Ready to lift your business?</h2>
          <p className="text-slate-500 mb-8 max-w-lg mx-auto font-medium">Connect with our engineering experts today for a customized consultation on your heavy-lifting needs.</p>
          <button className="bg-orange-500 text-white font-black px-10 py-4 rounded-2xl hover:bg-slate-900 transition-all shadow-xl shadow-orange-200 uppercase italic tracking-tighter flex items-center gap-2 mx-auto group">
            Consult Our Experts
            <Settings className="group-hover:rotate-180 transition-transform duration-700" size={20} />
          </button>
        </motion.div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPage;